import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef } from 'react';

// material ui
import { useTheme } from '@mui/material/styles';

import maplibregl from 'maplibre-gl';

export interface MapPinIcon {
  /** URL to a custom image to use as the marker. */
  iconUrl?: string;
  /** Width and height of the icon in pixels. Defaults to [25, 41] for images, [20, 20] for color. */
  iconSize?: [number, number];
  /**
   * Named color or any CSS color string used to render a simple colored circle pin.
   * Ignored when `iconUrl` is provided.
   * @example 'red' | '#1976d2' | 'rgb(0,128,0)'
   */
  color?: string;
}

export interface MapPin {
  latitude: number;
  longitude: number;
  /** Optional popup text shown when the pin is clicked */
  label?: string;
  /** Optional icon style for this pin */
  icon?: MapPinIcon;
}

interface LocationMapProps {
  pins: MapPin[];
  /** Height of the map container. Defaults to '400px' */
  height?: string;
  /** Initial zoom level used when there is exactly one pin. Defaults to 16 */
  zoom?: number;
}

/** Creates a DOM element used as a custom MapLibre marker */
const createMarkerElement = (icon?: MapPinIcon): HTMLElement | null => {
  if (!icon) return null;

  if (icon.iconUrl) {
    const [w, h] = icon.iconSize ?? [25, 41];
    const img = document.createElement('img');
    img.src = icon.iconUrl;
    img.style.width = `${w}px`;
    img.style.height = `${h}px`;
    img.style.display = 'block';
    return img;
  }

  if (icon.color) {
    const [w, h] = icon.iconSize ?? [20, 20];
    const div = document.createElement('div');
    div.style.cssText = `
      width:${w}px;
      height:${h}px;
      background:${icon.color};
      border:2px solid rgba(0,0,0,0.4);
      border-radius:50%;
      box-shadow:0 1px 4px rgba(0,0,0,0.4);
      cursor:pointer;
    `;
    return div;
  }

  return null;
};

const LocationMap = ({
  pins,
  height = '400px',
  zoom = 16,
}: LocationMapProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupStyleRef = useRef<HTMLStyleElement | null>(null);

  // Initialise the map once
  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
      },
      center: [0, 0],
      zoom: 2,
    });

    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    mapRef.current = map;

    // Inject a scoped style tag for popup theming
    const styleEl = document.createElement('style');
    containerRef.current?.appendChild(styleEl);
    popupStyleRef.current = styleEl;

    return () => {
      map.remove();
      mapRef.current = null;
      popupStyleRef.current = null;
    };
  }, []);

  // Update popup styles whenever the MUI theme changes
  useEffect(() => {
    const styleEl = popupStyleRef.current;
    if (!styleEl) return;

    const bg = theme.palette.background.paper;
    const text = theme.palette.text.primary;
    const border = theme.palette.divider;

    styleEl.textContent = `
      .maplibregl-popup-content {
        background: ${bg} !important;
        color: ${text} !important;
        border: 1px solid ${border};
        box-shadow: ${theme.shadows[4]};
        border-radius: ${theme.shape.borderRadius}px;
        font-family: ${theme.typography.fontFamily};
        font-size: ${theme.typography.body2.fontSize};
      }
      .maplibregl-popup-tip {
        border-top-color: ${bg} !important;
        border-bottom-color: ${bg} !important;
      }
    `;
  }, [theme]);

  // Sync pins whenever they change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || pins.length === 0) return;

    const markers: maplibregl.Marker[] = pins.map((pin) => {
      const el = createMarkerElement(pin.icon);
      const marker = new maplibregl.Marker(el ? { element: el } : undefined)
        .setLngLat([pin.longitude, pin.latitude])
        .addTo(map);

      if (pin.label) {
        marker.setPopup(
          new maplibregl.Popup({ offset: 25 }).setText(pin.label),
        );
      }

      return marker;
    });

    // Fit / center the view to show all pins
    if (pins.length === 1) {
      map.flyTo({ center: [pins[0].longitude, pins[0].latitude], zoom });
    } else {
      const bounds = pins.reduce(
        (b, pin) => b.extend([pin.longitude, pin.latitude]),
        new maplibregl.LngLatBounds(
          [pins[0].longitude, pins[0].latitude],
          [pins[0].longitude, pins[0].latitude],
        ),
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
    }

    return () => {
      markers.forEach((m) => m.remove());
    };
  }, [pins, zoom]);

  return <div ref={containerRef} style={{ height, width: '100%' }} />;
};

export default LocationMap;
